import { useEffect, useState, type CSSProperties } from 'react';

type DialogViewportStyle = CSSProperties & {
  '--dialog-mobile-height'?: string;
  '--dialog-mobile-top'?: string;
};

/** Keep mobile dialog actions above the on-screen keyboard without changing page layout. */
export function useMobileDialogViewport(active: boolean): DialogViewportStyle | undefined {
  const [style, setStyle] = useState<DialogViewportStyle>();

  useEffect(() => {
    if (!active) {
      setStyle(undefined);
      return;
    }
    const viewport = window.visualViewport;
    if (!viewport) return;
    const mobile = window.matchMedia('(max-width: 639px)');
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (!mobile.matches) {
          setStyle(undefined);
          return;
        }
        const height = `${Math.round(viewport.height)}px`;
        const top = `${Math.round(viewport.offsetTop)}px`;
        setStyle((previous) =>
          previous?.['--dialog-mobile-height'] === height &&
          previous?.['--dialog-mobile-top'] === top
            ? previous
            : { '--dialog-mobile-height': height, '--dialog-mobile-top': top },
        );
      });
    };
    update();
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    mobile.addEventListener('change', update);
    return () => {
      cancelAnimationFrame(frame);
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
      mobile.removeEventListener('change', update);
    };
  }, [active]);

  return style;
}
